import { createAsyncThunk, createSlice, createSelector } from '@reduxjs/toolkit';
import { adminService, usersService } from '../../services';

/**
 * Feature Toggles Slice
 * Fetches and manages feature toggles from the database
 */

// Fetch root-level feature toggles (admin only, for copying)
export const fetchRootFeatureToggles = createAsyncThunk(
  'featureToggles/fetchRoot',
  async (_, { getState }) => {
    try {
      const state = getState();
      const user = state.auth?.user;

      if (user?.role === 'admin' || user?.role === 'super-admin') {
        const response = await adminService.listRootFeatureToggles();
        if (response.success) {
          return response.data?.toggles || response.toggles || [];
        }
        throw new Error(response.message || 'Failed to fetch root feature toggles');
      }
      return [];
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('Failed to fetch root feature toggles:', error.message);
      }
      return [];
    }
  }
);

// Fetch user's resolved feature toggles (user-facing, no admin required)
export const fetchFeatureToggles = createAsyncThunk(
  'featureToggles/fetchAll',
  async (_, { getState }) => {
    try {
      // Check if user is admin - if so, use admin endpoint
      const state = getState();
      const user = state.auth?.user;

      if (user?.role === 'admin' || user?.role === 'super-admin') {
        // Admin users can use admin endpoint
        try {
          const response = await adminService.listFeatureToggles('all');
          if (response.success) {
            return response.data?.toggles || response.toggles || [];
          }
          throw new Error(response.message || 'Failed to fetch feature toggles');
        } catch (adminError) {
          // If admin endpoint fails, fall back to user endpoint
          console.warn('Admin endpoint failed, falling back to user endpoint:', adminError.message);
          const response = await usersService.getMyFeatureToggles();
          if (response.success) {
            return response.data?.toggles || response.toggles || [];
          }
          throw adminError;
        }
      } else {
        // Regular users use user-facing endpoint
        const response = await usersService.getMyFeatureToggles();
        if (response.success) {
          return response.data?.toggles || response.toggles || [];
        }
        throw new Error(response.message || 'Failed to fetch feature toggles');
      }
    } catch (error) {
      // Don't fail completely - return empty array if 403 (permission denied) or 404 (endpoint not found)
      if (
        error.status === 403 ||
        error.response?.status === 403 ||
        error.status === 404 ||
        error.response?.status === 404
      ) {
        if (process.env.NODE_ENV === 'development') {
          console.warn(
            'Access denied or endpoint not found for feature toggles, returning empty array'
          );
        }
        return [];
      }
      // For other errors, still return empty array to prevent app breakage
      if (process.env.NODE_ENV === 'development') {
        console.warn('Failed to fetch feature toggles:', error.message);
      }
      return [];
    }
  }
);

// Fetch plan-specific feature toggles
export const fetchPlanFeatureToggles = createAsyncThunk(
  'featureToggles/fetchPlanToggles',
  async (planId, { rejectWithValue }) => {
    try {
      const response = await adminService.getPlanFeatureToggles(planId);
      if (response.success) {
        return {
          planId,
          toggles: response.data?.toggles || response.toggles || [],
        };
      }
      throw new Error(response.message || 'Failed to fetch plan feature toggles');
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch plan feature toggles');
    }
  }
);

const initialState = {
  globalToggles: [],
  planToggles: {}, // { planId: [toggles] }
  loading: false,
  error: null,
};

const featureTogglesSlice = createSlice({
  name: 'featureToggles',
  initialState,
  reducers: {
    clearPlanToggles: (state, action) => {
      const planId = action.payload;
      if (planId) {
        delete state.planToggles[planId];
      } else {
        state.planToggles = {};
      }
    },
  },
  extraReducers: builder => {
    builder
      // Fetch root toggles
      .addCase(fetchRootFeatureToggles.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchRootFeatureToggles.fulfilled, (state, action) => {
        state.loading = false;
        state.rootToggles = action.payload;
      })
      .addCase(fetchRootFeatureToggles.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch all toggles
      .addCase(fetchFeatureToggles.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchFeatureToggles.fulfilled, (state, action) => {
        state.loading = false;
        state.globalToggles = action.payload;

        // Debug logging
        if (process.env.NODE_ENV === 'development') {
          const pageToggles = action.payload.filter(t => t.level === 'page');
          console.log(
            `[Redux] Feature toggles loaded: ${action.payload.length} total, ${pageToggles.length} page-level`
          );
          pageToggles.slice(0, 3).forEach(t => {
            console.log(`  - ${t.feature_key}: enabled=${t.enabled}, source=${t.source}`);
          });
        }
      })
      .addCase(fetchFeatureToggles.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch plan toggles
      .addCase(fetchPlanFeatureToggles.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPlanFeatureToggles.fulfilled, (state, action) => {
        state.loading = false;
        state.planToggles[action.payload.planId] = action.payload.toggles;
      })
      .addCase(fetchPlanFeatureToggles.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { clearPlanToggles } = featureTogglesSlice.actions;
export const selectGlobalToggles = state => state.featureToggles.globalToggles;
export const selectRootToggles = state => state.featureToggles.rootToggles;

// Memoized selector for plan toggles to prevent unnecessary re-renders
const selectPlanTogglesState = state => state.featureToggles.planToggles;
export const selectPlanToggles = createSelector(
  [selectPlanTogglesState, (state, planId) => planId],
  (planToggles, planId) => {
    const toggles = planToggles[planId];
    // Return the same empty array reference if no toggles exist
    return toggles || [];
  }
);

export const selectFeatureTogglesLoading = state => state.featureToggles.loading;

export default featureTogglesSlice.reducer;
